import React from "react";
import {useInventory} from "../State/InventoryContext";
import {GameItems, SMELTER_RECIPES} from "../State/GameData";
import {PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend} from "recharts";
import {ItemIcon, getItemIconConfig} from "../SharedComponents/ItemIcon";
import {ASSEMBLY_RECIPES} from "../State/AssemblyRecipes";
import {useDashboardStats} from "./dashboardCalculations";
import {
    DashboardContainer,
    DashboardGrid,
    DashboardCard,
    CardTitle,
    StatRow,
    StatLabel,
    StatValue,
    BigStat,
    BigStatValue,
    BigStatLabel,
    ChartContainer,
    ResourceGroup,
    GroupTitle,
} from "./DashboardStyles";

export function Dashboard() {
    const {inventory} = useInventory();

    // Calculate all dashboard statistics using the custom hook
    const stats = useDashboardStats(inventory);

    return (
        <DashboardContainer>
            <h1>Factory Dashboard</h1>

            <DashboardGrid>
                {/* Research Counter */}
                <DashboardCard>
                    <BigStat>
                        <BigStatValue>{stats.researchCount}</BigStatValue>
                        <BigStatLabel>Technologies Unlocked</BigStatLabel>
                    </BigStat>
                </DashboardCard>

                {/* Total Items Crafted */}
                <DashboardCard>
                    <BigStat>
                        <BigStatValue>{stats.totalCrafted}</BigStatValue>
                        <BigStatLabel>Total Items Crafted</BigStatLabel>
                    </BigStat>
                </DashboardCard>

                {/* Resource Inventory Pie Chart */}
                {stats.resources.length > 0 && (
                    <DashboardCard style={{gridColumn: "1 / -1"}}>
                        <CardTitle>Resource Inventory</CardTitle>
                        <ChartContainer>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={stats.resources as any}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={100}
                                        label={({name, value}) => `${name}: ${value}`}
                                    >
                                        {stats.resources.map((entry) => {
                                            const config = getItemIconConfig(entry.itemId);
                                            return (
                                                <Cell
                                                    key={entry.itemId}
                                                    fill={config?.color ?? "#3b82f6"}
                                                />
                                            );
                                        })}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </DashboardCard>
                )}

                {/* Basic Materials Histogram */}
                {stats.basicMaterials.length > 0 && (
                    <DashboardCard style={{gridColumn: "1 / -1"}}>
                        <CardTitle>Basic Materials Inventory</CardTitle>
                        <ChartContainer>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stats.basicMaterials}>
                                    <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        interval={0}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#2563eb">
                                        {stats.basicMaterials.map((entry) => {
                                            const config = getItemIconConfig(entry.itemId);
                                            return (
                                                <Cell
                                                    key={entry.itemId}
                                                    fill={config?.color ?? "#2563eb"}
                                                />
                                            );
                                        })}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </DashboardCard>
                )}

                {/* Mining Stats */}
                <DashboardCard>
                    <CardTitle>Mining Production</CardTitle>
                    <ResourceGroup>
                        <StatRow style={{background: "#f3f4f6", fontWeight: 600}}>
                            <StatLabel>Total Mined</StatLabel>
                            <StatValue>{stats.totalMinedPerSecond.toFixed(2)}/s</StatValue>
                        </StatRow>
                    </ResourceGroup>
                    <ResourceGroup>
                        <GroupTitle>Per Resource</GroupTitle>
                        {Object.entries(stats.miningStats).map(([resourceType, stat]) => {
                            const item = GameItems[resourceType];
                            return (
                                <StatRow key={resourceType}>
                                    <StatLabel>
                                        <ItemIcon itemId={resourceType} size={24} />
                                        {item?.name ?? resourceType}
                                    </StatLabel>
                                    <StatValue>{stat.total.toFixed(2)}/s</StatValue>
                                </StatRow>
                            );
                        })}
                    </ResourceGroup>
                </DashboardCard>

                {/* Smelting Stats */}
                <DashboardCard>
                    <CardTitle>Smelting Production</CardTitle>
                    <ResourceGroup>
                        <StatRow style={{background: "#f3f4f6", fontWeight: 600}}>
                            <StatLabel>Total Smelted</StatLabel>
                            <StatValue>{stats.totalSmeltedPerSecond.toFixed(2)}/s</StatValue>
                        </StatRow>
                    </ResourceGroup>
                    <ResourceGroup>
                        <GroupTitle>Per Resource</GroupTitle>
                        {Object.entries(stats.smeltingStats).map(([resourceType, stat]) => {
                            const recipe = SMELTER_RECIPES[resourceType];
                            const outputItem = recipe ? GameItems[recipe.outputId] : undefined;
                            return (
                                <StatRow key={resourceType}>
                                    <StatLabel>
                                        <ItemIcon itemId={recipe?.outputId ?? resourceType} size={24} />
                                        {outputItem?.name ?? resourceType}
                                    </StatLabel>
                                    <StatValue>{stat.total.toFixed(2)}/s</StatValue>
                                </StatRow>
                            );
                        })}
                    </ResourceGroup>
                </DashboardCard>

                {/* Power Stats */}
                <DashboardCard>
                    <CardTitle>Power Generation</CardTitle>
                    <StatRow>
                        <StatLabel>Current Supply</StatLabel>
                        <StatValue style={{color: "#10b981"}}>{stats.power.current.toFixed(1)} MW</StatValue>
                    </StatRow>
                    <StatRow>
                        <StatLabel>Maximum Capacity</StatLabel>
                        <StatValue>{stats.power.max.toFixed(1)} MW</StatValue>
                    </StatRow>
                    <StatRow>
                        <StatLabel>Current Demand</StatLabel>
                        <StatValue style={{color: stats.power.demand > stats.power.current ? "#ef4444" : "#10b981"}}>
                            {stats.power.demand.toFixed(1)} MW
                        </StatValue>
                    </StatRow>
                    <StatRow>
                        <StatLabel>Utilization</StatLabel>
                        <StatValue>
                            {stats.power.max > 0
                                ? ((stats.power.current / stats.power.max) * 100).toFixed(0)
                                : 0}%
                        </StatValue>
                    </StatRow>
                </DashboardCard>

                {/* Assembly Stats */}
                <DashboardCard style={{gridColumn: stats.assemblerCount > 0 ? "1 / -1" : "auto"}}>
                    <CardTitle>Assembly Production</CardTitle>
                    <StatRow>
                        <StatLabel>Total Assemblers</StatLabel>
                        <StatValue>{stats.assemblerCount}</StatValue>
                    </StatRow>
                    <StatRow>
                        <StatLabel>Running</StatLabel>
                        <StatValue style={{color: "#10b981"}}>{stats.runningAssemblers}</StatValue>
                    </StatRow>
                    <StatRow>
                        <StatLabel>Idle</StatLabel>
                        <StatValue style={{color: "#ef4444"}}>
                            {stats.assemblerCount - stats.runningAssemblers}
                        </StatValue>
                    </StatRow>

                    {Object.keys(stats.assemblerRecipes).length > 0 && (
                        <ResourceGroup style={{marginTop: 15}}>
                            <GroupTitle>Assembler Recipe Distribution</GroupTitle>
                            {Object.entries(stats.assemblerRecipes).map(([recipeId, count]) => {
                                const recipe = ASSEMBLY_RECIPES[recipeId];
                                if (!recipe) return null;
                                return (
                                    <StatRow key={recipeId}>
                                        <StatLabel>
                                            <ItemIcon itemId={recipe.result.itemId} size={24} />
                                            {recipe.name}
                                        </StatLabel>
                                        <StatValue>{count} assemblers</StatValue>
                                    </StatRow>
                                );
                            })}
                        </ResourceGroup>
                    )}

                    {Object.keys(stats.assemblyItemFlow).length > 0 && (
                        <ResourceGroup style={{marginTop: 15}}>
                            <GroupTitle>Item Consumption & Flow Analysis</GroupTitle>
                            {Object.entries(stats.assemblyItemFlow)
                                .sort(([, a], [, b]) => (b.consumed + b.produced) - (a.consumed + a.produced))
                                .map(([itemId, flow]) => {
                                    const item = GameItems[itemId];
                                    return (
                                        <div key={itemId} style={{marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #f3f4f6"}}>
                                            <StatRow style={{borderBottom: "none", paddingBottom: 4}}>
                                                <StatLabel>
                                                    <ItemIcon itemId={itemId} size={24} />
                                                    <strong>{item?.name ?? itemId}</strong>
                                                </StatLabel>
                                            </StatRow>
                                            <div style={{paddingLeft: 32, fontSize: 13, color: "#6b7280"}}>
                                                {flow.consumed > 0 && (
                                                    <div>• Consumed by {flow.consumed} assembler{flow.consumed > 1 ? "s" : ""}</div>
                                                )}
                                                {flow.produced > 0 && (
                                                    <div>• Produced by {flow.produced} assembler{flow.produced > 1 ? "s" : ""}</div>
                                                )}
                                                {flow.destinations.length > 0 && (
                                                    <div>• Flows to: {flow.destinations.join(", ")}</div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                        </ResourceGroup>
                    )}
                </DashboardCard>
            </DashboardGrid>
        </DashboardContainer>
    );
}
